import React from 'react';
import { getMemberInfo } from '@/hooks/useMembers';
import MAvatar from '@/components/ui/atoms/MAvatar';
import { useAvatar } from '@/hooks/useAvatar';
import { MButton } from '@/components/ui/atoms/MButtons';
import { useBackNavigation } from '@/hooks/useBackNavigation';
import HealthStory from './components/HealthStory';
import HealthEvents from './components/HealthEvents';
import styles from './dashboard.module.css';
import { useState, useEffect } from 'react';
import { useDashboard } from '@/hooks/useDashboard';
import DocumentsCard from './components/DocumentsCard';
import { FILE_CATEGORY_LABELS, mapHealthEventsForDisplay } from './dashboard.types';
import MSpacer from '@/components/ui/atoms/MSpacer';

import { useLocation, useNavigate } from 'react-router-dom';
import { useYearWindow } from '@/hooks/useYearWindow';

const YEARS_WINDOW = 5;

const DashboardDetail = () => {
  const memberInfo = getMemberInfo();
  const { avatarUrl } = useAvatar();
  const memberId = memberInfo.id;
  const navigate = useNavigate();
  const { summary, details, loading, error, fetchSummary, fetchDetails } = useDashboard(memberId);
  const location = useLocation();
  const state = location.state as { windowStartIdx?: number; selectedYear?: number };

  const {
    visibleYears,
    selectedYear,
    setSelectedYear,
    handleNext,
    handlePrev,
    showPrev,
    showNext,
    windowSize
  } = useYearWindow(
    summary,
    0,
    (location.state as any)?.selectedYear,
    YEARS_WINDOW,
    { fetchSummary },
    memberInfo.dobYear || 1940
  );

  // Use selectedYear from navigation state if present, else default to current year
  const initialYear = (location.state as any)?.selectedYear || new Date().getFullYear();

  // Scroll main container to top on mount - delayed to avoid conflict with browser scroll restoration
  React.useEffect(() => {
    const timer = setTimeout(() => {
      document.querySelector('main')?.scrollTo(0, 0);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Consolidated initialization and data fetching
  React.useEffect(() => {
    localStorage.removeItem("documentFilters");
    
    // Restore selectedYear from sessionStorage or use initialYear
    const savedYear = sessionStorage.getItem('dashboardDetailSelectedYear');
    const yearToUse = savedYear ? Number(savedYear) : initialYear;
    
    if (yearToUse !== selectedYear) {
      setSelectedYear(yearToUse);
    }
    
    // Initial fetch
    if (memberId) {
      fetchSummary(yearToUse);
      fetchDetails(yearToUse);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  // Persist selectedYear to sessionStorage and fetch details when year changes
  React.useEffect(() => {
    if (selectedYear) {
      sessionStorage.setItem('dashboardDetailSelectedYear', String(selectedYear));
      // Only fetch if this is not the initial mount (memberId useEffect handles initial fetch)
      if (memberId) {
        fetchDetails(selectedYear);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  return (
    <div className="min-h-screen flex flex-col px-6">
      <div className="dashboard-ellipses">
          <div className="dashboard-ellipse dashboard-ellipse-1"></div>
          <div className="dashboard-ellipse dashboard-ellipse-2"></div>
          <div className="dashboard-ellipse dashboard-ellipse-3"></div>
          <div className="dashboard-ellipse dashboard-ellipse-4"></div>
          <div className="dashboard-ellipse dashboard-ellipse-5"></div>
        </div>
      <div className={styles.dashboardProfileRow} style={{marginBottom: '36px'}}>
        <MButton  
          variant="back"
          onClick={useBackNavigation().handleBack}
          className="mr-2"
        >
          Back
        </MButton>
        <button onClick={() => navigate('/profile/')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
          <MAvatar
            text={`${memberInfo.firstName || ''}${memberInfo.lastName ? ' ' + memberInfo.lastName : ''}`.trim()}
            size="md"
            type={memberInfo.avatar ? 'dp' : 'no-dp'}
            src={avatarUrl || undefined}
            alt={`${memberInfo.firstName || ''} ${memberInfo.lastName || ''}`.trim()}
            memberId={memberInfo.id}
          />
        </button>
      </div>
          <HealthStory
          minimal
          years={visibleYears.map(year => {
            const y = summary.find(s => s.year === year) || {};
            return {
              year,
              hasHealthEvent: !!y.healthEventsCount,
              hasDocument: !!y.documentsCount,
            };
          })}
          selectedYear={selectedYear}
          onYearSelect={setSelectedYear}
          onPrev={showPrev ? handlePrev : undefined}
          onNext={showNext ? handleNext : undefined}
        />
      
      <MSpacer mt={40} />
        <HealthEvents
          year={selectedYear}
          events={mapHealthEventsForDisplay(details?.healthEvents)}
        />
      <MSpacer mt={40} />
      <DocumentsCard
        year={selectedYear}
        data={
          (details?.documentsCounts || []).map(doc => ({
            label: FILE_CATEGORY_LABELS[doc.category] || 'Other',
            count: doc.count,
          }))
        }
        onPrev={showPrev ? () => setSelectedYear(selectedYear - 1) : undefined}
        onNext={showNext ? () => setSelectedYear(selectedYear + 1) : undefined}
      />
      <MSpacer mt={80} />
    </div>
  );
};

export default DashboardDetail;
